import { motion } from "framer-motion";

export default function About() {
  const timeline = [
    { year: "2019", text: "Founded as a small trading company focusing on WinnerVPS." },
    { year: "2020", text: "Expanded into Chemical and Technology industries." },
    { year: "2023", text: "Established global partnerships across multiple industries." },
    { year: "2025", text: "Leading provider of Technology solutions for business growth." },
  ];

  const values = [
    {
      title: "Trust",
      desc: "Building reliable partnerships through integrity and consistency.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 sm:w-12 h-10 sm:h-12 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="url(#gradient)"
          strokeWidth={2}
        >
          <defs>
            <linearGradient id="gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#dfdfdfff" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
    {
      title: "Competitiveness",
      desc: "Offering valuable solutions with efficiency and fair pricing.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 sm:w-12 h-10 sm:h-12 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="url(#gradient)"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M12 3v18" />
        </svg>
      ),
    },
    {
      title: "Quality",
      desc: "Delivering high standard products for lasting satisfaction.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 sm:w-12 h-10 sm:h-12 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="url(#gradient)"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8l3 3-3 3m0-6l-3 3 3 3M4 12h16" />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-16 sm:py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-6"
        >
          About Us
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-gray-700 dark:text-gray-300 mb-12 text-sm sm:text-base leading-relaxed"
        >
          We are a general trading company providing high quality products and services
          across multiple industries, including technology and multi sector industries.
          Our mission is to deliver sustainable solutions that support the growth of our
          clients and partners.
        </motion.p>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="p-6 sm:p-8 bg-gradient-to-r from-green-100 to-blue-100 dark:bg-gray-800/60 dark:backdrop-blur-md dark:border dark:border-white/10 rounded-2xl shadow-lg text-left transition-all duration-300"
          >
            <h3 className="text-xl sm:text-2xl font-semibold text-blue-600 mb-4">
              🌟 Vision
            </h3>
            <p className="text-slate-800 dark:text-gray-800 text-sm sm:text-base leading-relaxed">
              To become a trusted global trading partner by delivering innovative,
              reliable, and sustainable solutions that empower industries and
              communities worldwide.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="p-6 sm:p-8 bg-gradient-to-r from-blue-100 to-green-100 dark:bg-gray-800/60 dark:backdrop-blur-md dark:border dark:border-white/10 rounded-2xl shadow-lg text-left transition-all duration-300"
          >
            <h3 className="text-xl sm:text-2xl font-semibold text-blue-600 mb-4">
              🚀 Mission
            </h3>
            <p className="text-slate-800 dark:text-gray-800 text-sm sm:text-base leading-relaxed">
              To provide high quality services and products across multiple industries
              while maintaining a commitment to innovation, sustainability, and
              customer satisfaction.
            </p>
          </motion.div>
        </div>

        {/* Our Values */}
        <div className="mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-8">
            Our Values
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 place-items-center">
            {values.map((val, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800/60 dark:backdrop-blur-md dark:border dark:border-white/10 rounded-2xl shadow-lg p-6 sm:p-8 text-center max-w-sm"
              >
                {val.icon}
                <h4 className="text-lg sm:text-xl font-semibold text-blue-600 dark:text-blue-400 mt-4 mb-2">
                  {val.title}
                </h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Company Journey */}
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-10">
            Our Journey
          </h3>

          <div className="relative border-l-4 border-blue-500 dark:border-blue-400 pl-6 sm:pl-8 space-y-8 max-w-3xl mx-auto text-left">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-2">
                  <span className="text-blue-600 dark:text-blue-400 font-bold text-lg sm:text-xl mr-3">
                    {item.year}
                  </span>
                  <div className="w-4 h-4 bg-blue-600 dark:bg-blue-400 rounded-full shadow-md"></div>
                </div>

                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
